// Billing constants for Next.js implementation
// Based on the Django billing constants

import type {
  BillingCycle,
  CheckoutPlanType,
} from "@ripenn/api-contracts/webapp/billing";

export const STRIPE_PRODUCT_ENV =
  process.env.STRIPE_PRODUCT_ENV || "development";

if (!["development", "production"].includes(STRIPE_PRODUCT_ENV)) {
  throw new Error(
    `STRIPE_PRODUCT_ENV must be one of ['development', 'production'], got: ${STRIPE_PRODUCT_ENV}`,
  );
}

// Product IDs. The development (test-mode) Starter and Growth products
// are the ones the lookup-key prices below belong to; the earlier
// prod_STrV* ids were deleted from the test account.
export const TEAM_PRODUCT_ID =
  STRIPE_PRODUCT_ENV === "production"
    ? "prod_StjOOETSpty3TI"
    : "prod_STrVNPhNH3OBco";

export const PRO_PRODUCT_ID =
  STRIPE_PRODUCT_ENV === "production"
    ? "prod_StjOBNBqYpqJD8"
    : "prod_U8VGFZdAzmE1Dn";

export const SOLO_PRODUCT_ID =
  STRIPE_PRODUCT_ENV === "production"
    ? "prod_StjOIHZptWKshn"
    : "prod_U8VFDOIWTUjkK2";

// Credit packs. One product holds every pack; each pack is an active
// one-time price on it whose `metadata.credits` says how many credits
// it buys. The product is created with this fixed id in both Stripe
// accounts (webapp/scripts/sync-credit-packs.ts), so no per-env
// branch is needed. Repricing is a Stripe change: create the new
// price and archive the old one; nothing here changes.
export const CREDITS_PRODUCT_ID = "ripenn_credits";

// Price lookup keys
//
// We address Stripe prices by lookup_key rather than price ID so the same
// constant works in both test and production Stripe accounts. Set the
// matching lookup_key on each price in Stripe (Dashboard → Product → Price,
// or `stripe prices create --lookup-key=...`). Resolved to a price ID at
// checkout time via lib/billing/utils.ts#getPriceIdByLookupKey.
//
// The "annual" lookup keys resolve to monthly-interval Stripe prices at the
// discounted rate. The 12-month commitment is enforced application-side
// via SubscriptionSchedule in the webhook handler (see
// app/api/billing/stripe-webhooks/route.ts).

// Starter (internal: solo) — $149/mo, $129/mo billed annually
export const SOLO_ANNUAL_LOOKUP_KEY = "starter_annual";
export const SOLO_MONTHLY_LOOKUP_KEY = "starter_monthly";

// Growth (internal: pro) — $499/mo, $399/mo billed annually
export const PRO_ANNUAL_LOOKUP_KEY = "growth_annual";
export const PRO_MONTHLY_LOOKUP_KEY = "growth_monthly";

// Enterprise (internal: team) — custom pricing, not used in self-serve checkout
export const TEAM_ANNUAL_LOOKUP_KEY = "enterprise_annual";
export const TEAM_MONTHLY_LOOKUP_KEY = "enterprise_monthly";

// Billing Products
// Internal DB values (solo/pro/team) are kept for backward compatibility.
// Use PLAN_DISPLAY_NAMES for user-facing labels.
export enum BillingProduct {
  /** No active subscription — fully blocked */
  FREE = "free",
  /** Starter tier ($149/mo) */
  SOLO = "solo",
  /** Growth tier ($499/mo) */
  PRO = "pro",
  /** Enterprise tier (custom) */
  TEAM = "team",
}

/**
 * User-facing plan names.
 * Internal values (solo/pro/team) map to marketing names (Starter/Growth/Enterprise).
 */
export const PLAN_DISPLAY_NAMES: Record<BillingProduct, string> = {
  [BillingProduct.FREE]: "No Plan",
  [BillingProduct.SOLO]: "Starter",
  [BillingProduct.PRO]: "Growth",
  [BillingProduct.TEAM]: "Enterprise",
};

/**
 * Purchasable billing products (excludes FREE — no subscription state)
 * Used for Stripe product mappings
 */
export type PurchasableBillingProduct = Exclude<
  BillingProduct,
  BillingProduct.FREE
>;

/**
 * Purchasable products in order from lowest to highest tier.
 * Used for determining upgrade paths and displaying plan options.
 */
export const PURCHASABLE_PRODUCTS_ORDERED: PurchasableBillingProduct[] = [
  BillingProduct.SOLO,
  BillingProduct.PRO,
  BillingProduct.TEAM,
];

// Billing Features (matches database usage_feature enum)
// Features removed from the product are kept for DB compatibility but marked deprecated.
export enum BillingFeature {
  CONTENT_GENERATIONS = "content_generations",
  /** @deprecated Removed from product */
  CONTENT_STRATEGY = "content_strategy",
  ANALYTICS = "analytics",
  /** @deprecated Removed from product — behind feature flag */
  EVALUATIONS = "evaluations",
  /** @deprecated Removed from product */
  PUBLISHING_INTEGRATIONS = "publishing_integrations",
  /** @deprecated Removed from product */
  COLLABORATIVE_EDITING = "collaborative_editing",
  CONTENT_IMPORT = "content_import",
  GAP_CONTENT = "gap_content",
  QUERY_DISCOVERY = "query_discovery",
  PROMPT_OPTIMIZATION = "prompt_optimization",
}

// Mapping objects (only purchasable products — excludes FREE)
export const BILLING_PRODUCT_TO_PRODUCT_ID_MAP: Record<
  PurchasableBillingProduct,
  string
> = {
  [BillingProduct.SOLO]: SOLO_PRODUCT_ID,
  [BillingProduct.PRO]: PRO_PRODUCT_ID,
  [BillingProduct.TEAM]: TEAM_PRODUCT_ID,
};

export const PRODUCT_ID_TO_BILLING_PRODUCT_MAP = Object.fromEntries(
  Object.entries(BILLING_PRODUCT_TO_PRODUCT_ID_MAP).map(([k, v]) => [v, k]),
);

// PLAN_MAP resolves a (plan, billing_cycle) pair to a Stripe price lookup_key.
// Convert to a price ID at checkout time via getPriceIdByLookupKey().
//
// Typed against the checkout contract's enums (type-only import — the
// contracts package never imports webapp code) so the accepted
// plan_type / billing_cycle sets can't drift from this map without a
// compile error here.
export const PLAN_MAP: Record<
  CheckoutPlanType,
  Record<BillingCycle, string>
> = {
  team: {
    annual: TEAM_ANNUAL_LOOKUP_KEY,
    monthly: TEAM_MONTHLY_LOOKUP_KEY,
  },
  pro: {
    annual: PRO_ANNUAL_LOOKUP_KEY,
    monthly: PRO_MONTHLY_LOOKUP_KEY,
  },
  solo: {
    annual: SOLO_ANNUAL_LOOKUP_KEY,
    monthly: SOLO_MONTHLY_LOOKUP_KEY,
  },
};

// Number of monthly billing iterations to commit a customer to when they
// choose the annual (discounted) cycle. Surfaced as Subscription metadata
// during checkout and consumed by the webhook handler.
export const ANNUAL_COMMIT_ITERATIONS = 12;
